// Settings → Packages — the customer-facing plan/package selection + checkout
// surface. Adapts the standalone /pricing page (PricingPanel) into a settings
// panel: an individual/business audience toggle, billing-interval tabs
// (monthly/quarterly/yearly, only those the catalog actually has), the current
// package highlighted, a one-off add-on / credit "fund" section, and Stripe
// Checkout + billing-portal CTAs.
//
// Catalog-driven (GET /billing/plans + /billing/addons); nothing here hardcodes
// a plan. No Clerk hooks — entitlements come from the fetch-backed store, so
// signed-out / non-Clerk desktop builds render without crashing.
import { useNavigate } from "@tanstack/react-router";
import { CheckIcon, MinusIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Card, CardFooter, CardHeader, CardPanel, CardTitle } from "../ui/card";
import { toastManager } from "../ui/toast";

import {
  fetchAddons,
  fetchPlans,
  startAddonCheckout,
  startCheckout,
  type BillingAddon,
  type BillingPlan,
} from "../../morrowBilling";
import { useEntitlements, startTrial } from "../../morrowEntitlements";
import {
  localizedBillingInterval,
  localizedCapabilityName,
  localizedPlanTagline,
  localizedPricingCategory,
} from "~/i18n/pricingLabels";
import {
  BILLING_INTERVALS,
  formatPrice,
  intervalPricing,
  maxIntervalDiscount,
  planCellValue,
  planFeatureLines,
  type BillingInterval,
} from "../billing/PricingPanel";
import type { EntitlementCapability } from "../../morrowEntitlements";
import { MorrowPricingSkeleton } from "../morrow/MorrowPageSkeletons";
import { searchableSetting } from "./settingsSearch";
import { SettingsPageContainer, SettingsSection } from "./settingsLayout";
import { useI18n } from "~/i18n";

type Audience = "individual" | "business";

// The three model tiers collapse into one "AI models" comparison row.
const MODEL_TIER_KEYS = new Set(["model.tier.pay", "model.tier.super", "model.tier.max"]);
// Order the capability sections appear as row groups (mirrors the server catalog).
const CATEGORY_ORDER = [
  "Models & AI",
  "Agents & automation",
  "Tools & connectors",
  "Browser & compute",
  "Storage & knowledge",
  "Collaboration & org",
] as const;

// Normalise a per-plan interval string into a stable billing-interval bucket.
type IntervalKey = string;

function intervalKey(interval: string): IntervalKey {
  const value = interval.trim().toLowerCase();
  if (value.includes("month")) return "month";
  if (value.includes("quart")) return "quarter";
  if (value.includes("year") || value.includes("annual")) return "year";
  return value || "month";
}

/** Short, human-friendly lines for an add-on's granted entitlements. */
function addonHighlights(addon: BillingAddon): string[] {
  return addon.entitlements
    .map((e) => {
      const cap = e.capability.replace(/\./g, " ");
      if (e.quantity !== null) return `+${e.quantity.toLocaleString()} ${cap}`;
      if (e.usageLimit !== null) return `+${e.usageLimit.toLocaleString()} ${cap}`;
      if (e.bool === true) return cap;
      return null;
    })
    .filter((line): line is string => line !== null);
}

function isPopularPlan(plan: BillingPlan): boolean {
  return plan.tier === "super" || plan.slug.endsWith("-ultra");
}

export function PackagesSettings() {
  const { t } = useI18n();
  const anchor = searchableSetting("packages", t);
  const entitlements = useEntitlements();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<ReadonlyArray<BillingPlan> | null | undefined>(undefined);
  const [addons, setAddons] = useState<ReadonlyArray<BillingAddon> | null | undefined>(undefined);
  const [audience, setAudience] = useState<Audience>("individual");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("month");
  const [busySlug, setBusySlug] = useState<string | null>(null);

  const lockedBusiness = entitlements.viewer.isOrg;
  useEffect(() => {
    if (lockedBusiness) setAudience("business");
  }, [lockedBusiness]);

  useEffect(() => {
    let cancelled = false;
    void fetchPlans().then((result) => {
      if (!cancelled) setPlans(result);
    });
    void fetchAddons().then((result) => {
      if (!cancelled) setAddons(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const audiencePlans = useMemo(
    () =>
      (plans ?? [])
        .filter((p) => p.audience === audience)
        .slice()
        .sort((a, b) => a.rank - b.rank),
    [plans, audience],
  );

  const allPlans = useMemo(() => {
    const byTier = new Map<string, BillingPlan>();
    for (const p of audiencePlans) {
      const existing = byTier.get(p.tier);
      if (!existing || intervalKey(p.interval) === "month") byTier.set(p.tier, p);
    }
    return [...byTier.values()].sort((a, b) => a.rank - b.rank);
  }, [audiencePlans]);

  const currentPlan = useMemo(
    () =>
      allPlans.find(
        (p) =>
          entitlements.isLoaded && p.tier === entitlements.tier && p.name === entitlements.planName,
      ) ?? null,
    [allPlans, entitlements.isLoaded, entitlements.tier, entitlements.planName],
  );

  const rowGroups = useMemo(() => {
    const caps = entitlements.capabilities;
    if (caps.length === 0) return [] as Array<{ category: string; rows: EntitlementCapability[] }>;
    const modelsRow: EntitlementCapability = {
      capability: "model.access",
      name: t("pricing.modelsRow"),
      description: t("pricing.modelsRowDescription"),
      type: "boolean",
      bool: true,
      granted: true,
      comingSoon: false,
      category: "Models & AI",
      unit: null,
      limit: null,
      used: 0,
      remaining: null,
      metered: false,
      planLimit: null,
      extra: 0,
    };
    const seen = new Map<string, EntitlementCapability[]>();
    for (const c of caps) {
      if (MODEL_TIER_KEYS.has(c.capability)) continue;
      const cat = c.category ?? "Other";
      if (!seen.has(cat)) seen.set(cat, []);
      seen.get(cat)!.push({
        ...c,
        name: localizedCapabilityName(c.capability, c.name, t),
      });
    }
    if (seen.has("Models & AI")) seen.set("Models & AI", [modelsRow, ...seen.get("Models & AI")!]);
    return CATEGORY_ORDER.filter((cat) => seen.has(cat)).map((category) => ({
      category: localizedPricingCategory(category, t),
      rows: seen.get(category)!,
    }));
  }, [entitlements.capabilities, t]);

  const onSelectPlan = useCallback(async (plan: BillingPlan) => {
    setBusySlug(plan.slug);
    if (plan.audience === "individual") {
      // Cardless trial of THIS plan's real entitlements. Only a definitive "ineligible" (trial
      // spent / already on a plan) falls through to Checkout; "trialing"/"error" never ask for a
      // card. On "error" (e.g. an org account can't start a personal trial — a 409) SHOW why,
      // instead of silently resetting and leaving the button looking broken.
      const result = await startTrial(plan.slug);
      if (result.outcome !== "ineligible") {
        if (result.outcome === "error") {
          toastManager.add({
            type: "error",
            title: "Couldn't start the free trial",
            description: result.message ?? "Please try again.",
          });
        }
        setBusySlug(null);
        return;
      }
    }
    const url = await startCheckout(plan.slug);
    if (url) {
      window.location.href = url;
      return;
    }
    toastManager.add({
      type: "error",
      title: "Couldn't start checkout",
      description:
        "This plan can't be started from here right now. Please try again or contact support.",
    });
    setBusySlug(null);
  }, []);

  const onSelectAddon = useCallback(async (addon: BillingAddon) => {
    setBusySlug(addon.slug);
    const url = await startAddonCheckout(addon.slug);
    if (url) {
      window.location.href = url;
      return;
    }
    setBusySlug(null);
  }, []);

  const manageBilling = (
    <Button variant="outline" size="sm" onClick={() => void navigate({ to: "/settings/billing" })}>
      {t("pricing.manageBilling")}
    </Button>
  );

  return (
    <SettingsPageContainer className="max-w-7xl" disableScrollFade>
      <SettingsSection id={anchor.id} title={anchor.title} headerAction={manageBilling}>
        <div className="space-y-8 px-3 sm:px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {lockedBusiness ? t("pricing.introBusiness") : t("pricing.introIndividual")}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {lockedBusiness ? null : (
                <AudienceToggle audience={audience} onChange={setAudience} />
              )}
              <BillingToggle interval={billingInterval} onChange={setBillingInterval} t={t} />
            </div>
          </div>

          {plans === undefined ? (
            <MorrowPricingSkeleton />
          ) : plans === null ? (
            <Hint>{t("pricing.unavailable")}</Hint>
          ) : allPlans.length === 0 ? (
            <Hint>{t("pricing.noPlans")}</Hint>
          ) : (
            <>
              <div className="-mx-3 overflow-x-auto px-3 py-2 sm:-mx-4 sm:px-4">
                <div className="grid min-w-[64rem] grid-cols-4 items-stretch gap-4 py-2">
                  {allPlans.map((plan) => (
                    <PlanCard
                      key={plan.slug}
                      plan={plan}
                      interval={billingInterval}
                      isCurrent={plan.slug === currentPlan?.slug}
                      isPopular={isPopularPlan(plan)}
                      busy={busySlug === plan.slug}
                      disabled={busySlug !== null}
                      onSelect={() => void onSelectPlan(plan)}
                      t={t}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold tracking-tight text-foreground">
                  {t("pricing.compareTitle")}
                </h3>
                <PlanComparisonTable
                  plans={allPlans}
                  rowGroups={rowGroups}
                  currentSlug={currentPlan?.slug ?? null}
                  busySlug={busySlug}
                  onSelectPlan={(plan) => void onSelectPlan(plan)}
                  t={t}
                />
              </div>
            </>
          )}
        </div>
      </SettingsSection>

      {addons === undefined || (addons !== null && addons.length > 0) ? (
        <SettingsSection id="packages-addons" title={t("pricing.addonsTitle")}>
          <div className="space-y-4 px-3 sm:px-4">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("pricing.addonsIntro")}
            </p>
            {addons === undefined ? (
              <Hint>{t("pricing.loadingAddons")}</Hint>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {(addons ?? []).map((addon) => (
                  <AddonCard
                    key={addon.slug}
                    addon={addon}
                    busy={busySlug === addon.slug}
                    disabled={busySlug !== null}
                    onSelect={() => void onSelectAddon(addon)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>
        </SettingsSection>
      ) : null}
    </SettingsPageContainer>
  );
}

function AudienceToggle({
  audience,
  onChange,
}: {
  readonly audience: Audience;
  readonly onChange: (audience: Audience) => void;
}) {
  const { t } = useI18n();
  const options: ReadonlyArray<{ value: Audience; label: string }> = [
    { value: "individual", label: t("pricing.individual") },
    { value: "business", label: t("pricing.business") },
  ];

  return (
    <div className="inline-flex rounded-lg border border-border bg-muted p-0.5 text-xs font-semibold">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-2.5 py-1 transition-colors",
            audience === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Hint({ children }: { readonly children: React.ReactNode }) {
  return (
    <p className="rounded-2xl border border-border bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

function ComparisonCell({
  value,
  t,
}: {
  readonly value: boolean | string;
  readonly t: ReturnType<typeof useI18n>["t"];
}) {
  if (value === true) {
    return (
      <CheckIcon className="mx-auto size-4 text-primary" aria-label={t("pricing.includedAria")} />
    );
  }
  if (value === false) {
    return (
      <MinusIcon
        className="mx-auto size-4 text-muted-foreground/50"
        aria-label={t("pricing.notIncludedAria")}
      />
    );
  }
  return <span className="font-medium tabular-nums text-foreground">{value}</span>;
}

const BILLING_INTERVAL_STYLES: Record<
  BillingInterval,
  { active: string; inactive: string; badgeActive: string; badgeInactive: string }
> = {
  month: {
    active: "bg-sky-500/15 text-sky-700 shadow-sm dark:text-sky-300",
    inactive:
      "text-sky-600/70 hover:bg-sky-500/10 hover:text-sky-700 dark:text-sky-400/70 dark:hover:text-sky-300",
    badgeActive: "bg-sky-500/20 text-sky-700 dark:text-sky-300",
    badgeInactive: "bg-sky-500/10 text-sky-600/80 dark:text-sky-400/80",
  },
  quarter: {
    active: "bg-amber-500/15 text-amber-700 shadow-sm dark:text-amber-300",
    inactive:
      "text-amber-600/70 hover:bg-amber-500/10 hover:text-amber-700 dark:text-amber-400/70 dark:hover:text-amber-300",
    badgeActive: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
    badgeInactive: "bg-amber-500/10 text-amber-600/80 dark:text-amber-400/80",
  },
  year: {
    active: "bg-emerald-500/15 text-emerald-700 shadow-sm dark:text-emerald-300",
    inactive:
      "text-emerald-600/70 hover:bg-emerald-500/10 hover:text-emerald-700 dark:text-emerald-400/70 dark:hover:text-emerald-300",
    badgeActive: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
    badgeInactive: "bg-emerald-500/10 text-emerald-600/80 dark:text-emerald-400/80",
  },
};

function BillingToggle({
  interval,
  onChange,
  t,
}: {
  readonly interval: BillingInterval;
  readonly onChange: (i: BillingInterval) => void;
  readonly t: ReturnType<typeof useI18n>["t"];
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
      {BILLING_INTERVALS.map((opt) => {
        const active = opt.key === interval;
        const save = maxIntervalDiscount(opt.key);
        const label = localizedBillingInterval(opt.key, t).label;
        const styles = BILLING_INTERVAL_STYLES[opt.key];
        return (
          <button
            key={opt.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.key)}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              active ? styles.active : styles.inactive,
            )}
          >
            {label}
            {save > 0 ? (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                  active ? styles.badgeActive : styles.badgeInactive,
                )}
              >
                {t("pricing.saveUpTo", { percent: save })}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function PlanCard({
  plan,
  interval,
  isCurrent,
  isPopular,
  busy,
  disabled,
  onSelect,
  t,
}: {
  readonly plan: BillingPlan;
  readonly interval: BillingInterval;
  readonly isCurrent: boolean;
  readonly isPopular: boolean;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly t: ReturnType<typeof useI18n>["t"];
}) {
  const isFree = plan.priceCents === 0;
  const pricing = intervalPricing(plan.priceCents, interval, plan.tier);
  const billedNote = localizedBillingInterval(interval, t).billedNote;
  const features = planFeatureLines(plan, t).slice(0, 4);

  const card = (
    <Card
      className={cn(
        "flex h-full flex-col transition-[box-shadow] duration-300 motion-reduce:transition-none",
        isPopular
          ? "pricing-ultra-card pricing-ultra-glass relative border-sky-500/25 shadow-lg shadow-sky-500/10 hover:border-sky-400/35 hover:shadow-xl hover:shadow-sky-500/20"
          : "hover:shadow-md",
        isCurrent &&
          !isPopular &&
          "border-primary/40 shadow-[0_0_0_1px_color-mix(in_oklab,var(--primary)_35%,transparent)]",
        isCurrent && isPopular && "ring-2 ring-sky-500/30",
      )}
    >
      <CardHeader className="relative z-[1] gap-3 pb-0">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{plan.name}</CardTitle>
          {isCurrent ? (
            <span className="rounded-md border border-primary/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
              {t("pricing.current")}
            </span>
          ) : isPopular ? (
            <span className="rounded-md border border-sky-500/35 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
              {t("pricing.popular")}
            </span>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">{localizedPlanTagline(plan.tier, t)}</p>
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="text-2xl font-semibold tabular-nums text-foreground">
            {isFree ? t("pricing.free") : formatPrice(pricing.perMonthCents, plan.currency)}
            {!isFree ? (
              <span className="text-sm font-medium text-muted-foreground">
                {t("pricing.perMonth")}
              </span>
            ) : null}
          </p>
          {!isFree && pricing.discountPct > 0 ? (
            <span className="rounded-md border border-primary/35 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              {t("pricing.discountOff", { percent: pricing.discountPct })}
            </span>
          ) : null}
        </div>
        {!isFree ? (
          <p className="text-xs text-muted-foreground">
            {t("pricing.billedPerMonth", { note: billedNote })}
          </p>
        ) : null}
      </CardHeader>

      <CardPanel className="relative z-[1] flex-1 pt-4">
        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          {features.map((line) => (
            <li key={line} className="flex items-start gap-2">
              <CheckIcon
                className={cn(
                  "mt-0.5 size-3.5 shrink-0",
                  isPopular ? "text-sky-500" : "text-primary",
                )}
                aria-hidden
              />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </CardPanel>

      <CardFooter className="relative z-[1] mt-auto w-full flex-col gap-2 pt-4">
        {isCurrent ? (
          <Button type="button" variant="outline" size="sm" className="w-full" disabled>
            {t("pricing.currentPlan")}
          </Button>
        ) : isFree ? (
          <Button type="button" variant="outline" size="sm" className="w-full" disabled>
            {t("pricing.included")}
          </Button>
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              className={cn(
                "w-full transition-[transform,box-shadow] duration-300 motion-reduce:transition-none",
                isPopular &&
                  "shadow-md shadow-sky-500/20 hover:shadow-lg hover:shadow-sky-500/25 active:scale-[0.98] motion-reduce:active:scale-100",
              )}
              onClick={onSelect}
              disabled={disabled}
            >
              {busy ? t("pricing.redirecting") : t("pricing.startTrial")}
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
              {t("pricing.trialNote", {
                price: formatPrice(pricing.perMonthCents, plan.currency),
              })}
            </p>
          </>
        )}
      </CardFooter>
    </Card>
  );

  if (!isPopular) return card;

  return <div className="pricing-ultra-shell h-full rounded-2xl">{card}</div>;
}

function AddonCard({
  addon,
  busy,
  disabled,
  onSelect,
  t,
}: {
  readonly addon: BillingAddon;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly t: ReturnType<typeof useI18n>["t"];
}) {
  return (
    <Card className="flex h-full flex-col transition-[box-shadow] duration-300 hover:shadow-md motion-reduce:transition-none">
      <CardHeader className="gap-2 pb-0">
        <CardTitle className="text-base">{addon.name}</CardTitle>
        <p className="text-2xl font-semibold tabular-nums text-foreground">
          {formatPrice(addon.priceCents, addon.currency)}
          {addon.interval ? (
            <span className="text-sm font-medium text-muted-foreground"> / {addon.interval}</span>
          ) : (
            <span className="text-sm font-medium text-muted-foreground">
              {" "}
              {t("pricing.oneOff")}
            </span>
          )}
        </p>
      </CardHeader>
      <CardPanel className="flex-1 pt-4">
        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          {addonHighlights(addon).map((line) => (
            <li key={line} className="flex items-start gap-2">
              <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </CardPanel>
      <CardFooter className="mt-auto w-full pt-4">
        <Button type="button" size="sm" className="w-full" onClick={onSelect} disabled={disabled}>
          {busy ? t("pricing.redirecting") : t("pricing.buy")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function PlanComparisonTable({
  plans,
  rowGroups,
  currentSlug,
  busySlug,
  onSelectPlan,
  t,
}: {
  readonly plans: ReadonlyArray<BillingPlan>;
  readonly rowGroups: ReadonlyArray<{
    category: string;
    rows: ReadonlyArray<EntitlementCapability>;
  }>;
  readonly currentSlug: string | null;
  readonly busySlug: string | null;
  readonly onSelectPlan: (plan: BillingPlan) => void;
  readonly t: ReturnType<typeof useI18n>["t"];
}) {
  const colClass = (slug: string) => (slug === currentSlug ? "bg-muted/40" : undefined);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="pb-3 pr-4 text-xs font-medium text-muted-foreground">
              {t("pricing.featureColumn")}
            </th>
            {plans.map((plan) => {
              const isCurrent = plan.slug === currentSlug;
              const isFree = plan.priceCents === 0;
              return (
                <th
                  key={plan.slug}
                  className={cn("px-2 pb-3 text-center align-top", colClass(plan.slug))}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      <span className="text-xs font-medium text-foreground">{plan.name}</span>
                      {isCurrent ? (
                        <span className="rounded-md border border-primary/40 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary">
                          {t("pricing.current")}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-lg font-semibold tabular-nums text-foreground">
                      {formatPrice(plan.priceCents, plan.currency)}
                      {!isFree ? (
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {" "}
                          / {plan.interval}
                        </span>
                      ) : null}
                    </div>
                    {isCurrent ? (
                      <span className="text-[11px] font-semibold text-muted-foreground">
                        {t("pricing.currentPlan")}
                      </span>
                    ) : isFree ? (
                      <span className="text-[11px] font-semibold text-muted-foreground">
                        {t("pricing.included")}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="xs"
                        className="w-full max-w-[8rem]"
                        onClick={() => onSelectPlan(plan)}
                        disabled={busySlug !== null}
                      >
                        {busySlug === plan.slug ? t("pricing.redirecting") : t("pricing.upgrade")}
                      </Button>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        {rowGroups.map((group) => (
          <tbody key={group.category} className="divide-y divide-border">
            <tr>
              <td
                colSpan={plans.length + 1}
                className="pt-6 pb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground"
              >
                {group.category}
              </td>
            </tr>
            {group.rows.map((cap) => (
              <tr key={cap.capability}>
                <td className="py-3 pr-4 text-foreground">{cap.name}</td>
                {plans.map((plan) => (
                  <td key={plan.slug} className={cn("px-2 py-3 text-center", colClass(plan.slug))}>
                    <ComparisonCell
                      value={planCellValue(plan, cap.capability, cap.type, t)}
                      t={t}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
